// cSpell: ignore changepassword Fullname
import { Component, ElementRef, inject, Injector, OnInit, ViewChild } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { SafeResourceUrl } from "@angular/platform-browser";
import { AppBarProfileButtonComponent } from "@tylertech/forge";
import { ForgeAppBarProfileButtonModule, ForgeBadgeModule } from "@tylertech/forge-angular";
import { ShellService } from "@tylertech/incode-module";
import { getLoggerFor, LogInvocation, LogLevel, ProjectReturnAs } from "@tylertech/logging";
import { LOCATION, Utilities } from "@tylertech/shared-module";
import { IPhoto, LoginViewModel, PhotoViewModel, SettingLookupOption } from "@tylertech/tyler-api-types";
import { ForgeAppBarProfileButtonAvatarColorDirective } from "../../../directives/forgeAvatarColor.directive";
import { LoginView } from "../../../interfaces/";
import { AuthorizeService, CachedRepositoryService, NavigationService, NotificationService, OmniBarService, OmniColorClass, RouteInfoService, SecurityService, StorageService, SystemInfoExtended, SystemInfoService, ToastService, UserProfileService } from "../../../services";
import { PasswordExpirationDialogComponent, PasswordExpirationDialogData } from "../../securityScreen";
import { createProfileCardElement, defineProfileCardComponent, ProfileCardOutput } from "./profileCard.component";

@Component({
    selector: "profile-menu",
    templateUrl: "./profileMenu.tmpl.html",
    styleUrls: [
        "./profileMenu.component.scss"
    ],
    imports: [
        ForgeAppBarProfileButtonModule,
        ForgeAppBarProfileButtonAvatarColorDirective,
        ForgeBadgeModule,
    ]
})
export class ProfileMenuComponent
    implements OnInit
{
    private readonly location = inject<Location>(LOCATION);
    private readonly matDialog = inject(MatDialog);
    private readonly $shellService = inject(ShellService);
    private readonly $toastService = inject(ToastService);
    private readonly $storageService = inject(StorageService);
    private readonly $authorizeService = inject(AuthorizeService);
    private readonly $systemInfoService = inject(SystemInfoService);
    private readonly $securityService = inject(SecurityService);
    private readonly $userProfileService = inject(UserProfileService);
    private readonly $navigationService = inject(NavigationService);
    private readonly $routeInfoService = inject(RouteInfoService);
    private readonly $repositoryService = inject(CachedRepositoryService);
    private readonly $omniBarService = inject(OmniBarService);
    private readonly $notificationService = inject(NotificationService);

    @ViewChild("omniProfile") private readonly omniProfile: ElementRef<AppBarProfileButtonComponent>;

    private readonly log = getLoggerFor("ProfileMenuComponent");
    private hasPhoto: boolean;
    private loginModel: LoginView;
    private profilePhoto: IPhoto;

    items: SettingLookupOption[];
    /**
     * This Promise tis created during ngOnInit and is only here to for Test to have something await on
     */
    protected initializing?: Promise<void>;

    version: string;
    notifications: number = 0;
    notificationCountKey = "notification_count";
    erpProAppUrl: string;

    readonly cardBuilder: () => HTMLElement;

    constructor()
    {
        const injector = inject(Injector);

        defineProfileCardComponent(injector);

        this.cardBuilder = this.profileCardBuilder.bind(this);
    }

    get photoUrl(): SafeResourceUrl
    {
        return this.hasPhoto
            ? `data:image/jpeg;base64,${this.profilePhoto.Photo}`
            : "";
    }

    get fullName(): string
    {
        return this.$securityService.principle?.Fullname ?? "NO_USER";
    }

    get avatarDisplayName(): string
    {
        return this.$securityService.principle?.AvatarDisplayName ?? this.fullName;
    }

    get username(): string
    {
        return this.$securityService.principle?.Username ?? "NO USER";
    }

    get passwordExpiring(): boolean
    {
        return !!this.$securityService.Settings?.pwExpMessage;
    }

    get pwExpMess(): string
    {
        return this.passwordExpiring
            ? this.$securityService.Settings.pwExpMessage as string
            : null;
    }

    get isVendorUser(): boolean
    {
        return this.$securityService.isVendorUser;
    }

    ngOnInit(): void
    {
        this.initializing = Promise.all(
            [
                this.loadLoginInfo(),
                this.loadSystemInfo()
            ])
            .then(([_, systemInfo]: [LoginViewModel, SystemInfoExtended]) =>
            {
                return this.checkPasswordOnLogin(systemInfo);
            })
            .finally(() =>
            {
                this.initializing = undefined;
            });
    }

    private checkPasswordOnLogin(systemInfo: SystemInfoExtended): Promise<void>
    {
        const newLogin = this.$securityService.isNewToken();
        if (newLogin
            && this.$securityService.Settings?.pwExpMessage
            && !systemInfo.useTylerId)
        {
            const matDialogConfig: MatDialogConfig<PasswordExpirationDialogData> = {
                data: {
                    expirationMessage: this.pwExpMess
                }
            };
            const dialogRef = this.matDialog.open(PasswordExpirationDialogComponent, matDialogConfig);

            return dialogRef.afterClosed()
                .toPromise()
                .then(
                    changePW =>
                    {
                        if (changePW)
                        {
                            this.goToChangePassword();
                        }
                    }
                );
        }
    }

    private goToChangePassword(): void
    {
        // FIXME: why aren't we using router.navigate to go to the core route for changepassword? This could change what app the user is in.
        // ? REVIEW: and who consumes these query parameters
        this.$navigationService.navigateToApp("form/changepassword/?path=UserProfileFormMetadata&b=1");
    }

    gotoProfile(): void
    {
        this.$navigationService.navigateToApp("form/?path=UserProfileFormMetadata&b=1");
    }

    @LogInvocation(LogLevel.DEBUG)
    private loadNotifications(): Promise<void>
    {
        return this.$repositoryService.Fetch("Api.Core.EF.CommonModel.Notification")
            .then(result =>
            {
                this.notifications = result.length;
                if (!Utilities.IsNullOrUndefined(this.$storageService.Get(this.notificationCountKey)) && this.notifications > +(this.$storageService.Get(this.notificationCountKey) ?? 0))
                    this.$toastService.showActionableToast("You have new notifications.", "View", () => this.viewNotifications());
                this.$storageService.Set(this.notificationCountKey, this.notifications);
            });
    }

    @LogInvocation(LogLevel.DEBUG, true)
    private loadLoginInfo(): Promise<LoginViewModel>
    {
        return this.$authorizeService.ReloadLoginInfo()
            .then(r =>
            {
                this.loginModel = r as LoginViewModel;
                if (this.$securityService.hasSetting("ProfileCardData"))
                {
                    this.items = this.$securityService.Settings["ProfileCardData"] as SettingLookupOption[];
                }

                this.$securityService.updateSettings(this.loginModel);

                if (this.$securityService.hasSetting("OmniBarColor"))
                {
                    this.$omniBarService.colorClass = this.$securityService.Settings.OmniBarColor as OmniColorClass;
                }

                return r;
            });
    }

    @LogInvocation(LogLevel.DEBUG, true)
    private loadPhoto(): Promise<PhotoViewModel>
    {
        if (!this.$securityService.isAuthenticated)
        {
            return null;
        }

        return this.$userProfileService.GetPhoto()
            .then((result: IPhoto) =>
            {
                if (result?.Photo)
                {
                    this.profilePhoto = result;
                    this.hasPhoto = true;
                    return result;
                }

                return null;
            });
    }

    @LogInvocation(LogLevel.DEBUG, true)
    private loadSystemInfo(): Promise<SystemInfoExtended>
    {
        return this.$systemInfoService.GetCachedSystemInfo()
            .then(systemInfo =>
            {
                this.version = systemInfo.DisplayVersion;
                const agent = window.navigator.userAgent;
                // Need to show this for ERP Pro 9 and 10
                // All non-CefSharp browsers and WebLauncher.
                if (systemInfo.Product.startsWith("Incode")
                    && (!this.$shellService.isChromium() || !!window["webLauncher"]))
                {
                    const hostName = this.location.hostname.toLowerCase();
                    let baseUrl = "";
                    switch (hostName)
                    {
                        case "express-qa.tylertech.com":
                        case "localhost":
                            baseUrl = "express-qa.tylertech.com";
                            break;
                        case "incode.tylerhost.net":
                        case "erppro.tylerapp.com":
                            baseUrl = "erppro.tylerapp.com";
                            break;
                        default:
                            break;
                    }

                    if (baseUrl
                        && this.$routeInfoService.tenant)
                    {
                        this.erpProAppUrl = `https://${baseUrl}/ERPProApp/setup.exe?Tenant=${this.$routeInfoService.tenant}`;
                    }
                }

                if (!systemInfo.IsTest())
                {
                    return Promise.all(
                        [
                            this.loadPhoto(),
                            this.loadNotifications()
                        ])
                        .then(() =>
                        {
                            return systemInfo;
                        });
                }

                return systemInfo;
            });
    }

    @LogInvocation(LogLevel.DEBUG, true)
    logout(): void
    {
        this.profilePhoto = null;
        this.hasPhoto = false;

        this.$securityService.logout(true);
    }

    @LogInvocation(LogLevel.DEBUG, true)
    @ProjectReturnAs((x: HTMLElement) => { return { element: x, outerHtml: x.outerHTML }; })
    private profileCardBuilder(): HTMLElement
    {
        const customElement = createProfileCardElement({
            items: this.items,
            version: this.version,
            passwordExpiring: this.passwordExpiring,
            notifications: this.notifications,
            showERPProAppDownload: !Utilities.IsNullOrUndefinedOrEmpty(this.erpProAppUrl)
        });

        customElement.addEventListener(ProfileCardOutput.ChangePassword, () =>
        {
            this.onChangePasswordClick();
        });

        customElement.addEventListener(ProfileCardOutput.ViewNotifications, () =>
        {
            this.viewNotifications();
        });

        customElement.addEventListener(ProfileCardOutput.downloadERPProApp, () =>
        {
            this.downloadERPProApp();
        });

        return customElement;
    }

    private onChangePasswordClick(): void
    {
        const changePwUrl = this.$navigationService.getLinkToApp("form/changepassword/?path=UserProfileFormMetadata&b=1") as string;
        window.open(changePwUrl, '_self');
    }

    private viewNotifications(): void
    {
        this.$notificationService.ViewNotifications();
    }

    private downloadERPProApp(): void
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(this.erpProAppUrl))
            return;

        window.open(this.erpProAppUrl, '_blank');
    }
}
